import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Scale, Truck, ShieldCheck, Recycle, Phone } from "lucide-react";
import Estimator from "@/components/Estimator";

export default function Home() {
  const [, setQuote] = useState(null);

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#1a1a1c] bg-black">
        <div className="absolute inset-0 wa-grid-bg pointer-events-none opacity-70" />
        <div className="wa-soft-glow" style={{ top: -120, left: -120 }} />
        <div className="wa-soft-glow" style={{ bottom: -160, right: -120 }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-12 gap-10 relative">
          <div className="lg:col-span-7 wa-fadeup">
            <span className="wa-chip wa-chip-green mb-6" data-testid="hero-eyebrow">
              <Scale size={14} /> UK rubbish removal · priced by weight
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-white">
              Estimate, book,<br />
              <span className="text-[#8DC63F]">we weigh & recycle.</span>
            </h1>
            <p className="mt-6 text-lg text-gray-300 max-w-xl">
              Get an instant estimate and book us in. We turn up, weigh your waste — you load it or we load it — then we take it away and recycle whatever we can.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/book" className="wa-btn-primary" data-testid="hero-book-collection-button">
                Book Collection <ArrowRight size={18} />
              </Link>
              <Link to="/pricing" className="wa-btn-outline" data-testid="hero-see-pricing-button">
                See pricing
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              <Feature icon={<Truck size={16} />} label="Self or we-load" />
              <Feature icon={<ShieldCheck size={16} />} label="Licensed carrier" />
              <Feature icon={<Recycle size={16} />} label="Recycled where we can" />
            </div>
          </div>

          <div className="lg:col-span-5 wa-fadeup" style={{ animationDelay: "120ms" }}>
            <Estimator compact onQuote={(q) => setQuote(q)} />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="how-it-works">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-10">
          <div className="lg:col-span-8">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500">How it works</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 text-white">
              Three honest steps, no surprises.
            </h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { n: "01", t: "Estimate", d: "Tell us roughly what you've got. We instantly quote based on weight, waste type and bulky items." },
            { n: "02", t: "Book a slot", d: "Pick from our available roadside or driveway slots and pay a small deposit." },
            { n: "03", t: "We weigh, you pay only for what we take", d: "Our crew arrives, weighs the load and your final price is calculated to the kilo." },
          ].map((s) => (
            <div key={s.n} className="wa-card relative">
              <div className="absolute -top-3 -left-3 bg-[#8DC63F] text-black text-xs font-black tracking-wider px-2 py-1 rounded-md">{s.n}</div>
              <h3 className="font-display text-xl font-bold mb-1 text-white">{s.t}</h3>
              <p className="text-sm text-gray-400">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Weigh Away (deeper dark band) */}
      <section className="bg-[#0a0a0a] border-y border-[#1a1a1c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Scale size={40} className="text-[#8DC63F]" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-5 text-white">
              Skip hire belongs in the past.
            </h2>
            <p className="text-gray-400 mt-4">
              Skips charge you for empty space, hidden weight caps, permits and missed days. We turn up,
              load if you want, and bill you on the actual weight.
            </p>
            <Link to="/book" className="wa-btn-primary mt-6" data-testid="dark-cta-book">
              Book Collection <ArrowRight size={18} />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-3">
            {[
              "No skip permits",
              "No weight cap penalties",
              "Self-load or we-load",
              "Per-kg transparent pricing",
              "Photos let us pre-quote",
              "Deposit secures slot",
            ].map((p) => (
              <div key={p} className="flex items-start gap-2 bg-white/5 border border-[#232428] rounded-xl p-4">
                <CheckCircle2 className="text-[#8DC63F] shrink-0 mt-0.5" size={18} />
                <span className="text-sm font-medium text-white">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-white">
          Got stuff? <span className="text-[#8DC63F]">Weigh it away.</span>
        </h2>
        <p className="text-gray-400 mt-3 max-w-xl mx-auto">
          Book online in under 2 minutes. Pay a small deposit, the rest after we weigh.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Link to="/book" className="wa-btn-primary" data-testid="bottom-book-cta">
            Book Collection <ArrowRight size={18} />
          </Link>
          <Link to="/contact" className="wa-btn-outline" data-testid="bottom-contact-cta">
            <Phone size={16} /> Call us
          </Link>
        </div>
      </section>
    </>
  );
}

function Feature({ icon, label }) {
  return (
    <div className="flex items-center gap-2 text-xs font-semibold text-gray-200">
      <div className="w-7 h-7 rounded-full bg-[#8DC63F] grid place-items-center text-black">{icon}</div>
      <span>{label}</span>
    </div>
  );
}
